import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { LandingPageCampaign } from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import { Alert, CardV2 as Card, Toggle } from '@feathr/components';
import { useFlags, useStore } from '@feathr/extender/state';

import { AddGoals } from '../../CampaignEditPage/StepGoals';
import LandingPageCampaignInfo from './LandingPageCampaignInfo';
import LandingPageCampaignPage from './LandingPageCampaignPage';

function SettingsLandingPageCampaignPage(): JSX.Element {
  const { t } = useTranslation();

  const { campaignId } = useParams<{ eventId: string; campaignId: string }>();
  const { Campaigns, Goals, Templates } = useStore();
  const { consentBannerEnabled: showConsentBannerSettings } = useFlags();
  const campaign = Campaigns.get(campaignId) as LandingPageCampaign;

  // A LandingPage campaign may have no template yet; `?.` keeps a template-less
  // campaign from crashing the page (Feathr/crowbar#545).
  const templateId = !campaign.isPending ? campaign.get('template')?.id : undefined;
  const template = templateId ? Templates.get(templateId) : undefined;

  const goals = Goals.list({
    filters: {
      _parent: campaignId,
      is_archived__ne: true,
    },
  });

  function handleConsentBannerChange(value?: boolean): void {
    template?.set({ is_consent_banner_enabled: value === true });
  }

  return (
    <LandingPageCampaignPage title={'Settings'}>
      <Card>
        <Card.Header title={t('Campaign Info')} />
        <Card.Content>
          <LandingPageCampaignInfo campaign={campaign} />
        </Card.Content>
      </Card>
      <Card>
        <Card.Header description={t('Configure the goals for this campaign.')} title={t('Goals')} />
        <Card.Content addVerticalGap={true}>
          <AddGoals goalKind={'campaign'} goals={goals.models} parent={campaign} />
        </Card.Content>
      </Card>
      {showConsentBannerSettings && template && !template.isPending && (
        <Card>
          <Card.Header
            description={t('Control how this page collects visitor tracking consent.')}
            title={t('Privacy')}
          />
          <Card.Content>
            {campaign.get('state') === CampaignState.Stopped && (
              <Alert
                title={t(
                  'This campaign is stopped. Saving a change to the consent banner publishes the page again.',
                )}
                type={'warning'}
              />
            )}
            <Toggle
              helpText={t(
                'Shown only to visitors in regions that require tracking consent. The banner is how visitors grant Feathr permission to collect data — without it, visitors in those regions are not tracked.',
              )}
              label={t('Show cookie consent banner')}
              layout={'well'}
              onChange={handleConsentBannerChange}
              value={template.get('is_consent_banner_enabled') ?? true}
            />
          </Card.Content>
        </Card>
      )}
    </LandingPageCampaignPage>
  );
}

export default observer(SettingsLandingPageCampaignPage);
